import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  TabsProps,
  Typography,
  useTheme,
} from '@mui/material'
import { Countries } from '@xoda/common'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilState } from 'recoil'
import { GoodsService, GoodsType } from '../services/goods'
import { PlanService } from '../services/plan'
import { gymState } from '../store/gym'
import { themeMeta } from '../theme/meta'
import { MoreLess } from './MoreLess'
import { StyledTab, StyledTabs } from './styled'

const GoodsNew: FC<GoodsNewProps> = ({ plans, packs, onSelect }) => {
  const { t } = useTranslation()
  const [gym] = useRecoilState(gymState)
  const theme = useTheme()

  const [activeGroupIndex, setActiveGroupIndex] = useState(0)
  const [selectedItem, setSelectedItem] = useState<GoodsType>()

  const createHandleItemClick = (item: GoodsType) => () => {
    setSelectedItem(item)
  }

  const handleTabsChange: Required<TabsProps>['onChange'] = useCallback(
    (e, value) => {
      setActiveGroupIndex(value)
    },
    []
  )

  const groups = useMemo(() => {
    if (!(plans && packs)) {
      return
    }

    const trialGroup = plans.filter(PlanService.isTrial.bind(PlanService))
    const packGroup = [
      ...packs,
      ...plans.filter(
        (plan) =>
          plan.version &&
          plan.details &&
          [4, 7].includes(plan.details.payment.payment_schedule)
      ),
    ]

    const membershipGroup = plans.filter(
      (plan) => !trialGroup.includes(plan) && !packGroup.includes(plan)
    )

    return [
      ...(trialGroup.length ? [{ title: t('Trials'), data: trialGroup }] : []),
      ...(membershipGroup.length
        ? [{ title: t('Memberships'), data: membershipGroup }]
        : []),
      ...(packGroup.length ? [{ title: t('Packs'), data: packGroup }] : []),
    ]
  }, [packs, plans, t])

  useEffect(() => {
    if (!groups) {
      return
    }

    const firstItem = groups[0]?.data[0]
    setSelectedItem(firstItem)
  }, [groups])

  useEffect(() => {
    if (!selectedItem) {
      return
    }

    onSelect?.(selectedItem)
  }, [onSelect, selectedItem])

  if (!groups) {
    return
  }

  const renderList = (items: GoodsType[]) => {
    return <MoreLess>{items.map(renderItem)}</MoreLess>
  }

  const renderItem = (item: GoodsType) => {
    const price = GoodsService.getPrice(item)

    return (
      <Box key={item.id} mb={2}>
        <Card
          variant="outlined"
          {...(selectedItem === item && {
            sx: { borderWidth: 2, borderColor: theme.palette.text.primary },
          })}
        >
          <CardActionArea onClick={createHandleItemClick(item)}>
            <CardContent>
              <Grid container justifyContent="space-between" mb={1}>
                <Grid>
                  <Typography variant="h4">{item.name}</Typography>
                  {GoodsService.isPack(item) && (
                    <Typography
                      variant="body2"
                      color={themeMeta.colors.monochrome.label}
                    >
                      {t('Valid for {{duration}}', {
                        duration: t(`${item.pack_type}WithCount`, {
                          count: item.pack_duration,
                        }),
                      })}
                    </Typography>
                  )}
                </Grid>
                <Grid>
                  {typeof price === 'number' ? (
                    <Typography variant="h4">
                      {Countries.formatAmount(price, gym?.currency_code, true)}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="h4">
                        {Countries.formatAmount(
                          price.number,
                          gym?.currency_code,
                          true
                        )}
                      </Typography>
                      {GoodsService.isPlan(item) &&
                      PlanService.isTrial(item) ? (
                        <Typography
                          variant="body2"
                          color={themeMeta.colors.monochrome.label}
                        >
                          {t('dayWithCount', { count: item.trial_days })}
                        </Typography>
                      ) : (
                        price.per && (
                          <Typography
                            variant="body2"
                            color={themeMeta.colors.monochrome.label}
                          >
                            /{price.per}
                          </Typography>
                        )
                      )}
                    </>
                  )}
                </Grid>
              </Grid>
              <Typography>{item.description}</Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      </Box>
    )
  }

  if (groups.length === 1) {
    return renderList(groups[0].data)
  }

  return (
    <>
      <StyledTabs value={activeGroupIndex} onChange={handleTabsChange}>
        {groups.map((group, index) => (
          <StyledTab label={group.title} value={index} />
        ))}
      </StyledTabs>
      {!!groups.length && renderList(groups[activeGroupIndex].data)}
    </>
  )
}

type GoodsNewProps = {
  plans?: Dto.Plan[]
  packs?: Dto.Pack[]
  onSelect?: (item: GoodsType) => void
}

export { GoodsNew }
export type { GoodsNewProps }
